import type { CrossSectionDataSet, PumpDataContext } from "./d";
import {
  generateEquipmentDataPoints,
  generateCrossSectionDataSetTitle,
  generateMLEDataPoints
} from "./calculateCrossSectionDataSets";
import { convertSmallLengthUnits } from "@/libs/units/length";
import { getYToolBypassPositioning } from "./getYToolChartDataSet";

/**
 * Generates datasets representing the pump cross-sections
 *
 * @param context The cross-section chart context
 * @returns An array of datasets representing the pumps
 */
export const getPumpChartDataSet = (
  pumpDataContext: PumpDataContext
): CrossSectionDataSet[][] => {
  const pumpChartSet: CrossSectionDataSet[][] = [];
  // Radius =$Inputs.F4/2, Center X, Center Y
  const casingInnerRadius = pumpDataContext.casing.innerDiameterIn / 2;
  const casingInnerCenterX = 0;
  const casingInnerCenterY = 0;
  const casingInnerPoints = generateEquipmentDataPoints(
    casingInnerRadius,
    casingInnerCenterX,
    casingInnerCenterY
  );
  const casingDriftRadius = pumpDataContext.casing.driftDiameterIn / 2;
  const casingDriftCenterX = 0;
  const casingDriftCenterY = 0;
  const casingDriftPoints = generateEquipmentDataPoints(
    casingDriftRadius,
    casingDriftCenterX,
    casingDriftCenterY
  );
  for (const pump of pumpDataContext.pumps) {
    const pumpChartDataSet: CrossSectionDataSet[] = [];
    pumpChartDataSet.push({
      title: generateCrossSectionDataSetTitle(
        "Casing ID",
        pumpDataContext.casing.innerDiameterIn,
        pumpDataContext.lengthUnit
      ),
      xAxisLabel: `${pumpDataContext.lengthUnit.label}`,
      yAxisLabel: `${pumpDataContext.lengthUnit.label}`,
      points: casingInnerPoints.map(pt => {
        return {
          x: convertSmallLengthUnits(pt.x, pumpDataContext.lengthUnit),
          y: convertSmallLengthUnits(pt.y, pumpDataContext.lengthUnit)
        };
      })
    });
    pumpChartDataSet.push({
      title: generateCrossSectionDataSetTitle(
        "Casing Drift ID",
        pumpDataContext.casing.driftDiameterIn,
        pumpDataContext.lengthUnit
      ),
      xAxisLabel: `${pumpDataContext.lengthUnit.label}`,
      yAxisLabel: `${pumpDataContext.lengthUnit.label}`,
      points: casingDriftPoints.map(pt => {
        return {
          x: convertSmallLengthUnits(pt.x, pumpDataContext.lengthUnit),
          y: convertSmallLengthUnits(pt.y, pumpDataContext.lengthUnit)
        };
      })
    });
    // --- Pump Dim ---
    // Radius=$Inputs.D15/2, Center X, Center Y
    const pumpDimRadius = pump.outerDiameterIn / 2;
    const maxEquipmentOuterDiameterIn =
      pumpDataContext.maxEquipmentOuterDiameterIn || 0;
    let pumpDimCenterX: number;
    const pumpDimCenterY = 0;

    // If there's a y tool we need to display different data sets
    if (
      pumpDataContext.hasYTool &&
      pumpDataContext.bypassTubingOuterDiameterIn &&
      pumpDataContext.yToolWidthIn
    ) {
      // Y-Tool Bypass Tubing Dim circle - use shared positioning logic
      const bypassPositioning = getYToolBypassPositioning({
        casingDriftRadius,
        yToolWidthIn: pumpDataContext.yToolWidthIn,
        bypassTubingOuterDiameterIn:
          pumpDataContext.bypassTubingOuterDiameterIn,
        lengthUnit: pumpDataContext.lengthUnit
      });
      pumpChartDataSet.push(bypassPositioning.bypassTubingDataSet);

      // Position pump using Excel formula: ='Y-Tool Cross'!K4
      // Max Equipment Center X = bypassCenterX + bypassRadius + maxEquipmentRadius
      const maxEquipmentDimRadius = maxEquipmentOuterDiameterIn / 2;
      pumpDimCenterX =
        bypassPositioning.bypassTubingDimCenterX +
        bypassPositioning.bypassTubingDimRadius +
        maxEquipmentDimRadius;
    } else {
      // Standard positioning when no y-tool
      pumpDimCenterX = -casingDriftRadius + maxEquipmentOuterDiameterIn / 2;
    }
    const pumpDimPoints = generateEquipmentDataPoints(
      pumpDimRadius,
      pumpDimCenterX,
      pumpDimCenterY
    );
    pumpChartDataSet.push({
      title: generateCrossSectionDataSetTitle(
        "Pump OD",
        pump.outerDiameterIn,
        pumpDataContext.lengthUnit
      ),
      xAxisLabel: `${pumpDataContext.lengthUnit.label}`,
      yAxisLabel: `${pumpDataContext.lengthUnit.label}`,
      points: pumpDimPoints.map(pt => {
        return {
          x: convertSmallLengthUnits(pt.x, pumpDataContext.lengthUnit),
          y: convertSmallLengthUnits(pt.y, pumpDataContext.lengthUnit)
        };
      })
    });
    // Only chart MLE and Pump + MLE OD when there's no y-tool
    if (!pumpDataContext.hasYTool) {
      // --- MLE Upper Dim ---
      // Radius =$Inputs.I13/2, (mle_depth_inches/2)
      const mleUpperDimRadius = pumpDataContext.mle.depthIn / 2;
      // Center X =G3+K3+G4 (pumpDimRadius + pumpDimCenterX + mleUpperDimRadius)
      const mleUpperDimCenterX =
        pumpDimRadius + mleUpperDimRadius + pumpDimCenterX;
      // Center Y = =$Inputs.H13/2-$Inputs.I13/2 === mle.mle_width_inches/2 - mle_depth_inches/2
      const mleUpperDimCenterY =
        pumpDataContext.mle.widthIn / 2 - pumpDataContext.mle.depthIn / 2;
      // --- MLE Lower Dim ---
      // Radius == mleUpperDimRadius
      const mleLowerDimRadius = mleUpperDimRadius;
      // Center X same as upper
      const mleLowerDimCenterX = mleUpperDimCenterX;
      // Center Y = =-($Inputs.H13/2+$Inputs.I13/2) === -(motorData.mle_width_inches/2 - mle_depth_inches/2)
      const mleLowerDimCenterY = -(
        pumpDataContext.mle.widthIn / 2 -
        pumpDataContext.mle.depthIn / 2
      );
      const mleDimPoints = generateMLEDataPoints(
        mleUpperDimRadius,
        mleUpperDimCenterX,
        mleUpperDimCenterY,
        mleLowerDimRadius,
        mleLowerDimCenterX,
        mleLowerDimCenterY
      );
      pumpChartDataSet.push({
        title: generateCrossSectionDataSetTitle(
          "MLE Depth",
          pumpDataContext.mle.depthIn,
          pumpDataContext.lengthUnit
        ),
        xAxisLabel: `${pumpDataContext.lengthUnit.label}`,
        yAxisLabel: `${pumpDataContext.lengthUnit.label}`,
        points: mleDimPoints.map(pt => {
          return {
            x: convertSmallLengthUnits(pt.x, pumpDataContext.lengthUnit),
            y: convertSmallLengthUnits(pt.y, pumpDataContext.lengthUnit)
          };
        })
      });
      // --- PUMP + MLE DIM ---
      // Radius =$Inputs.L15/2
      const maxCalculatedOuterDiameterIn =
        pump.maxCalculatedOuterDiameterIn || 0;
      const pumpAndMleDimRadius = maxCalculatedOuterDiameterIn / 2;
      // Center X =Q3-G3+G4
      const pumpAndMleDimCenterX =
        pumpAndMleDimRadius - pumpDimRadius + pumpDimCenterX;
      const pumpAndMleDimCenterY = 0;
      const pumpAndMleDimPoints = generateEquipmentDataPoints(
        pumpAndMleDimRadius,
        pumpAndMleDimCenterX,
        pumpAndMleDimCenterY
      );
      pumpChartDataSet.push({
        title: generateCrossSectionDataSetTitle(
          "Pump + MLE OD",
          maxCalculatedOuterDiameterIn,
          pumpDataContext.lengthUnit
        ),
        xAxisLabel: `${pumpDataContext.lengthUnit.label}`,
        yAxisLabel: `${pumpDataContext.lengthUnit.label}`,
        points: pumpAndMleDimPoints.map(pt => {
          return {
            x: convertSmallLengthUnits(pt.x, pumpDataContext.lengthUnit),
            y: convertSmallLengthUnits(pt.y, pumpDataContext.lengthUnit)
          };
        })
      });
    }
    pumpChartSet.push(pumpChartDataSet);
  }
  return pumpChartSet;
};
